import {useCallback} from "react";

import {GuideStateApi} from "@khan/guide-state";
import type {AIGuideMetadata} from "@khan/guide-types";
import {Errors, KAError} from "@khan/logging";

import type {GuideComponents} from "../component-types.ts";
import {useSendGuideItem} from "../internal/use-send-guide-item.ts";
import {langfuseSessionURL} from "../utils/traces.ts";

type Result<TOutput> =
    | {ok: false; error: Error}
    | {
          ok: true;
          output: TOutput;
          interactionId: string;
          sessionUrl: string;
          conversationEntries: AIGuideMetadata["conversationEntries"];
      };

interface RunGuideComponentOptions {
    generateVoice?: boolean;
}

type SendGuideItemEntryComponentFn = <
    ComponentName extends keyof GuideComponents,
    ComponentInput = GuideComponents[ComponentName]["input"],
>(
    componentName: ComponentName,
    input: ComponentInput,
    options?: RunGuideComponentOptions,
) => Promise<AIGuideMetadata>;

/**
 * Hook for obtaining a function to send a guide item of type "entry_component"
 * to the backend. The promise returned from the callback resolves after the
 * sendGuideItem call completes but it resolves with AIGuideMetadata which
 * contains the component output and other sendGuideItem output.
 */
const useSendEntryComponentGuideItem = (): SendGuideItemEntryComponentFn => {
    const sendGuideItem = useSendGuideItem();

    const sendGuideItemEntryComponent = useCallback(
        async <
            ComponentName extends keyof GuideComponents,
            ComponentInput = GuideComponents[ComponentName]["input"],
        >(
            componentName: ComponentName,
            input: ComponentInput,
            options?: RunGuideComponentOptions,
        ): Promise<AIGuideMetadata> => {
            const {generateVoice} = options ?? {};

            let lastMetadata: AIGuideMetadata | null = null;
            let lastError: Error | null = null;

            await sendGuideItem(
                "entry_component",
                {
                    entryComponent: {
                        name: componentName,
                        input: input ?? {},
                    },
                },
                {
                    generateVoice,
                    onResponseComplete(metadata) {
                        lastMetadata = metadata;
                    },
                    onResponseFailed(err) {
                        lastError = err;
                    },
                },
            );

            if (lastError != null) {
                throw lastError;
            }

            if (!lastMetadata) {
                throw new KAError("No metadata received", Errors.Internal);
            }

            return lastMetadata;
        },
        [sendGuideItem],
    );

    return sendGuideItemEntryComponent;
};
export default function useRunGuideComponent<
    ComponentName extends keyof GuideComponents,
    ComponentOutput = GuideComponents[ComponentName]["output"],
    ComponentInput = GuideComponents[ComponentName]["input"],
>(componentName: ComponentName) {
    const sendEntryComponentGuideItem = useSendEntryComponentGuideItem();

    const runComponent: (
        input: ComponentInput,
        options?: RunGuideComponentOptions,
    ) => Promise<Result<ComponentOutput>> = useCallback(
        async (input: ComponentInput, options?: RunGuideComponentOptions) => {
            GuideStateApi.set("isLoading", true);

            try {
                const metadata = await sendEntryComponentGuideItem(
                    componentName,
                    input,
                    options,
                );

                const {
                    entryComponentOutput,
                    threadId,
                    interactionId,
                    conversationEntries,
                } = metadata;

                return {
                    ok: true,
                    output: entryComponentOutput as ComponentOutput,
                    interactionId,
                    sessionUrl: langfuseSessionURL(threadId),
                    conversationEntries,
                };
            } catch (err) {
                return {ok: false, error: err as Error};
            } finally {
                GuideStateApi.set("isLoading", false);
            }
        },
        [sendEntryComponentGuideItem, componentName],
    );

    return runComponent;
}
